import { Box, Button, Typography } from '@montugroup/design-system';
import { Skeleton, styled, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { Link as RouterLink } from 'react-router-dom';

import Error from '@/components/error/Error';
import OrderHistoryCard from '@/components/order/order-history-card/OrderHistoryCard';
import OrderHistoryCardLoading from '@/components/order/order-history-card/OrderHistoryCardLoading';
import type { PatientOrder } from '@/hooks/patient/useGetPatientOrders';
import useGetPatientOrders from '@/hooks/patient/useGetPatientOrders';

import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import { FF_BRAZE_CONTENT_CARDS } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { ESCRIPT_REFILL_ROUTE } from '@/routes/route-paths';
import constants from '../../../constants/constants';

export const RECENT_ORDER_MONTHS_THRESHOLD = 1;

const getPastOrderMonth = (yearMonth: string) => DateTime.fromFormat(yearMonth, 'yyyy-MM').toFormat('MMMM yyyy');

const OrderHistoryCardContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));

const PastOrderMonthHeadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.primary.light,
  borderRadius: theme.shape.borderRadius,
}));

const PageHeadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(8),
  paddingBottom: theme.spacing(3),
  flexDirection: 'column',
  [`${theme.breakpoints.up('lg')}`]: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
}));

const activeStatuses = [
  constants.orderStatus.PENDING,
  constants.orderStatus.PHARMACY_PENDING,
  constants.orderStatus.AWAITING_PAYMENT,
  constants.orderStatus.PAID_NOT_APPROVED,
  constants.orderStatus.APPROVED_NOT_PAID,
  constants.orderStatus.AWAITING_SHIPMENT,
  constants.orderStatus.AWAITING_DISPENSE,
  constants.orderStatus.AWAITING_TOKEN,
  constants.orderStatus.PROCESSING_DISPENSE,
  constants.orderStatus.PHARMACY_DISPENSED,
  constants.orderStatus.INTERVAL_TIME,
  constants.orderStatus.SHIPPED,
  constants.orderStatus.PROCESSING_DISPENSE,
  constants.orderStatus.PHARMACY_OUT_OF_STOCK,
  constants.orderStatus.PARTIALLY_OUT_OF_STOCK,
  constants.orderStatus.PMS_PATIENT_PROFILE_INCOMPLETE,
];

function PatientOrdersLoading() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ padding: '1rem 0' }}>
        <Typography variant="h5" fontWeight="bold">
          <Skeleton />
        </Typography>
      </Box>
      <OrderHistoryCardContainer>
        <OrderHistoryCardLoading />
      </OrderHistoryCardContainer>
      <OrderHistoryCardContainer>
        <OrderHistoryCardLoading />
      </OrderHistoryCardContainer>
      <OrderHistoryCardContainer>
        <OrderHistoryCardLoading />
      </OrderHistoryCardContainer>
    </Box>
  );
}

export default function PatientOrders() {
  const { data: orders, isError, isLoading } = useGetPatientOrders();
  const theme = useTheme();
  const { flags } = useFeatureFlags();

  if (isLoading) {
    return <PatientOrdersLoading />;
  }

  if (isError) {
    return <Error status={500} />;
  }

  const activeOrders: PatientOrder[] = [];
  const pastOrders = new Map<string, PatientOrder[]>();
  const recentOrderThreshold = DateTime.now().minus({ months: RECENT_ORDER_MONTHS_THRESHOLD });

  orders?.forEach((order: PatientOrder) => {
    const orderDate = DateTime.fromISO(order.order_date);
    const orderDateIsBeforeCutoff = orderDate <= recentOrderThreshold;
    if (activeStatuses.includes(order.orderStatus) && !order.delivered && !orderDateIsBeforeCutoff) {
      activeOrders.push(order);
    } else {
      // map past orders, in desc, grouped by year and month
      const mapKey = orderDate.toFormat('yyyy-MM');
      if (!pastOrders.has(mapKey)) {
        pastOrders.set(mapKey, []);
      }
      pastOrders.get(mapKey)?.push(order);
    }
  });

  const pastOrdersMapKeys = Array.from(pastOrders.entries());
  return (
    <>
      <Box sx={{ width: '100%' }}>
        {flags[FF_BRAZE_CONTENT_CARDS] && (
          <Box sx={{ paddingBottom: theme.spacing(6) }}>
            <BrazeImageCard />
          </Box>
        )}
        <PageHeadingContainer>
          <Button variant="contained" color="secondary" component={RouterLink} to={ESCRIPT_REFILL_ROUTE}>
            Order Medication
          </Button>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Active Orders
            </Typography>
          </Box>
        </PageHeadingContainer>
        <Box>
          {activeOrders.length ? (
            activeOrders.map((order, index) => (
              <OrderHistoryCardContainer key={order.id}>
                <OrderHistoryCard
                  orderNumber={order.order_code}
                  cancelledDate={order.order_history_cancelled_date}
                  numberOfItems={order.OrderProducts.length}
                  orderDate={new Date(order.order_date)}
                  pharmacy={order.pharmacy}
                  orderPrice={order.total_price}
                  orderStatus={order.orderStatus}
                  delivered={order.delivered}
                  trackingLink={order.shippingTracking?.trackingUrl}
                  dispensingFee={order.dispensing_fee}
                  splitOrder={!order.orderStatus}
                  defaultExpanded={index === 0}
                  deliveryOption={order.deliveryOption}
                />
              </OrderHistoryCardContainer>
            ))
          ) : (
            <Box sx={(theme) => ({ padding: `${theme.spacing(5)} 0` })}>
              <Typography>You have no active orders</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Box sx={(theme) => ({ padding: `${theme.spacing(3)} 0` })}>
          <Typography variant="h5" fontWeight="bold">
            Past Orders
          </Typography>
        </Box>
        {pastOrders.size ? (
          pastOrdersMapKeys.map(([key, groupedOrders]) => (
            <OrderHistoryCardContainer key={key}>
              <PastOrderMonthHeadingContainer>
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.primary.contrastText })}
                  fontWeight="bold"
                >
                  {getPastOrderMonth(key).toUpperCase()}
                </Typography>
              </PastOrderMonthHeadingContainer>
              {groupedOrders.map((order) => (
                <OrderHistoryCardContainer key={order.id}>
                  <OrderHistoryCard
                    orderNumber={order.order_code}
                    numberOfItems={order.OrderProducts.length}
                    orderDate={new Date(order.order_date)}
                    pharmacy={order.pharmacy}
                    orderPrice={order.total_price}
                    orderStatus={order.orderStatus}
                    delivered={order.delivered}
                    trackingLink={order.shippingTracking?.trackingUrl}
                    dispensingFee={order.dispensing_fee}
                    splitOrder={!order.orderStatus}
                    cancelledDate={order.order_history_cancelled_date}
                    defaultExpanded={false}
                    deliveryOption={order.deliveryOption}
                  />
                </OrderHistoryCardContainer>
              ))}
            </OrderHistoryCardContainer>
          ))
        ) : (
          <Box sx={(theme) => ({ padding: `${theme.spacing(5)} 0` })}>
            <Typography>You have no past orders</Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
